/**
 * Comments API — branch-scoped comments with spatial anchoring.
 */

import { apiClient } from "./client";

export type AnchorType = "plot_point" | "surface_point" | "cell";

export interface PlotPointAnchor {
  type: "plot_point";
  x_value: number;
  y_value: number;
  series?: string;
}

export interface SurfacePointAnchor {
  type: "surface_point";
  x: number;
  y: number;
  z: number;
  face_id?: number;
  normal?: [number, number, number];
}

export interface CellAnchor {
  type: "cell";
  row_index: number;
  column: string;
}

export type CommentAnchor = PlotPointAnchor | SurfacePointAnchor | CellAnchor;

export interface Reaction {
  emoji: string;
  count: number;
  user_ids: string[];
}

export interface Comment {
  id: string;
  branch: string;
  target_type: string;
  target_id: string;
  parent_id: string | null;
  author_id: string;
  author_name: string | null;
  author_avatar: string | null;
  body: string;
  anchor: CommentAnchor | null;
  context_snapshot: Record<string, unknown> | null;
  resolved: boolean;
  resolved_by: string | null;
  resolved_at: string | null;
  merged_from: string | null;
  created_at: string;
  updated_at: string | null;
  reactions: Reaction[];
}

export interface CommentsResponse {
  total: number;
  comments: Comment[];
}

export interface CommentCreatePayload {
  target_type: string;
  target_id: string;
  branch: string;
  body: string;
  parent_id?: string;
  anchor?: CommentAnchor;
}

export async function fetchComments(
  projectId: string,
  params: {
    target_type: string;
    target_id: string;
    branch?: string;
    include_all_branches?: boolean;
    limit?: number;
    offset?: number;
  },
): Promise<CommentsResponse> {
  const sp = new URLSearchParams({
    target_type: params.target_type,
    target_id: params.target_id,
    branch: params.branch ?? "main",
  });
  if (params.include_all_branches) sp.set("include_all_branches", "true");
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.offset) sp.set("offset", String(params.offset));
  return apiClient<CommentsResponse>(`/projects/${projectId}/comments?${sp}`);
}

export async function createComment(
  projectId: string,
  payload: CommentCreatePayload,
): Promise<{ id: string; created_at: string }> {
  return apiClient(`/projects/${projectId}/comments`, {
    method: "POST",
    body: payload,
  });
}

export async function updateComment(
  projectId: string,
  commentId: string,
  payload: { body?: string; resolved?: boolean },
): Promise<{ id: string; updated: boolean }> {
  return apiClient(`/projects/${projectId}/comments/${commentId}`, {
    method: "PATCH",
    body: payload,
  });
}

export async function deleteComment(
  projectId: string,
  commentId: string,
): Promise<void> {
  await apiClient(`/projects/${projectId}/comments/${commentId}`, {
    method: "DELETE",
  });
}

export async function toggleReaction(
  projectId: string,
  commentId: string,
  emoji: string,
): Promise<{ comment_id: string; emoji: string; action: "added" | "removed" }> {
  return apiClient(`/projects/${projectId}/comments/${commentId}/reactions`, {
    method: "POST",
    body: { emoji },
  });
}

export async function fetchUnresolvedSummary(
  projectId: string,
  branch = "main",
): Promise<{ total: number; affected_targets: number }> {
  return apiClient(`/projects/${projectId}/comments/unresolved?branch=${encodeURIComponent(branch)}`);
}

export async function fetchCommentCounts(
  projectId: string,
  branch: string,
  nodeIds?: string[],
): Promise<Record<string, number>> {
  const sp = new URLSearchParams({ branch });
  if (nodeIds?.length) sp.set("node_ids", nodeIds.join(","));
  return apiClient(`/projects/${projectId}/comments/counts?${sp}`);
}
